import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Rating,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Container
} from '@mui/material';
import {
  ArrowBack,
  Save,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const EditSupplier = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [supplier, setSupplier] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchSupplier();
  }, [id]);

  const fetchSupplier = async () => {
    try {
      const response = await suppliersAPI.getById(id);
      setSupplier(response.data.data.supplier);
      setFormData(response.data.data.supplier);
    } catch (error) {
      enqueueSnackbar('Error fetching supplier', { variant: 'error' });
      navigate('/hospital/pharmacy/suppliers');
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await suppliersAPI.update(id, formData);
      enqueueSnackbar('Supplier updated successfully!', { variant: 'success' });
      navigate('/hospital/pharmacy/suppliers');
    } catch (error) {
      enqueueSnackbar('Error updating supplier', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!supplier) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/pharmacy/suppliers')}
            variant="outlined"
          >
            Back to Suppliers
          </Button>
          <Typography variant="h3" fontWeight="bold" sx={{ flexGrow: 1 }}>
            Edit Supplier
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Basic Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Supplier Name"
                        value={formData.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Contact Person"
                        value={formData.contactPerson || ''}
                        onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="email"
                        label="Email"
                        value={formData.email || ''}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={formData.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Alternate Phone"
                        value={formData.alternatePhone || ''}
                        onChange={(e) => handleInputChange('alternatePhone', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Business Information */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Business Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="GST Number"
                        value={formData.gstNumber || ''}
                        onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="PAN Number"
                        value={formData.panNumber || ''}
                        onChange={(e) => handleInputChange('panNumber', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Drug License Number"
                        value={formData.drugLicenseNumber || ''}
                        onChange={(e) => handleInputChange('drugLicenseNumber', e.target.value)}
                        required
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Address & Additional Info */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Address Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Street Address"
                        value={formData.address?.street || ''}
                        onChange={(e) => handleInputChange('address.street', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="City"
                        value={formData.address?.city || ''}
                        onChange={(e) => handleInputChange('address.city', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="State"
                        value={formData.address?.state || ''}
                        onChange={(e) => handleInputChange('address.state', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="ZIP Code"
                        value={formData.address?.zipCode || ''}
                        onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Country"
                        value={formData.address?.country || 'India'}
                        onChange={(e) => handleInputChange('address.country', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Supplier Settings */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    Supplier Settings
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        select
                        label="Payment Terms"
                        value={formData.paymentTerms || '30_days'}
                        onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                      >
                        <MenuItem value="advance">Advance</MenuItem>
                        <MenuItem value="7_days">7 Days</MenuItem>
                        <MenuItem value="15_days">15 Days</MenuItem>
                        <MenuItem value="30_days">30 Days</MenuItem>
                        <MenuItem value="45_days">45 Days</MenuItem>
                        <MenuItem value="60_days">60 Days</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Lead Time (Days)"
                        value={formData.leadTime || 7}
                        onChange={(e) => handleInputChange('leadTime', parseInt(e.target.value))}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6" component="legend">Supplier Rating</Typography>
                      <Rating
                        value={formData.rating || 3}
                        onChange={(event, newValue) => {
                          handleInputChange('rating', newValue);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.isActive !== undefined ? formData.isActive : true}
                            onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          />
                        }
                        label={<Typography variant="h6">Active Supplier</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Notes"
                        value={formData.notes || ''}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
            <Button
              onClick={() => navigate('/hospital/pharmacy/suppliers')}
              variant="outlined"
              size="large"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={<Save />}
            >
              {loading ? 'Updating...' : 'Update Supplier'}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
};